import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Row, Col, Collapse } from 'reactstrap';

import apiOffers from 'api/offers';
import maximize from 'assets/maximize.svg';
import minimize from 'assets/minimize.svg';
import IOfferRevision from 'api/interfaces/offers/IOfferRevision';
import getOfferStatusLabel from 'services/offers/offerStatusLabel';
import BasicTableNoPagination from 'components/layout/tables/BasicTableNoPagination';
import ModalConfirm from 'components/layout/modals/ModalConfirm';
import PrimaryButton from 'components/layout/buttons/PrimaryButton';
import translations from 'services/translations/translations';
import { ADDITIONAL_OFFER } from 'services/enums/offers/offerTypes';
import { RIGHT } from 'services/enums/sides';
import { addMessage } from 'store/messages/actions';

const formatDate = (dateTime: null | string): string => {
  if (dateTime === null || dateTime === undefined) {
    return '';
  }

  const [year, month, day] = dateTime.split(' ')[0].split('-');
  return `${day}.${month}.${year}`;
};

const OfferRevisions = ({
  canCreate = true,
  offerId,
  onRevisionsLoaded,
  type,
}: {
  // Users outside the NDA of the offer cannot create revisions
  canCreate?: boolean;
  offerId: number;
  onRevisionsLoaded?: (count: number) => void;
  type: string;
}): JSX.Element | null => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation([translations.offers, translations.common]);

  const [revisions, setRevisions] = useState<IOfferRevision[]>([]);
  const [confirmationIsOpen, setConfirmationIsOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  // Closed by default so that the list does not take space on the offer page
  const [isOpen, setIsOpen] = useState(false);

  const isAdditionalOffer = type === ADDITIONAL_OFFER;

  useEffect(() => {
    const getRevisions = async () => {
      try {
        const loaded = isAdditionalOffer
          ? await apiOffers.additionalOffers.revisions.get(offerId)
          : await apiOffers.revisions.get(offerId);

        setRevisions(loaded);
        onRevisionsLoaded?.(loaded.length);
      } catch (error) {
        setRevisions([]);
      }
    };

    getRevisions();
  }, [offerId, isAdditionalOffer]);

  const currentRevision = revisions.find((revision) => revision.id === offerId);

  const createRevision = async () => {
    setConfirmationIsOpen(false);

    if (creating) {
      return;
    }

    setCreating(true);

    try {
      const result = isAdditionalOffer
        ? await apiOffers.additionalOffers.revisions.post(offerId)
        : await apiOffers.revisions.post(offerId);

      dispatch(addMessage(t(`${translations.offers}:Revision created`)));
      history.push(`/offers/${result.id}/${type}`);
    } catch (error) {
      dispatch(addMessage(t(`${translations.offers}:Revision failed`)));
    } finally {
      setCreating(false);
    }
  };

  const displayedRevisions = revisions.map((revision) => ({
    created: formatDate(revision.created),
    id: revision.id,
    revision:
      revision.id === offerId
        ? `${revision.revisionNumber} (${t(
            `${translations.offers}:Current revision`
          )})`
        : revision.revisionNumber,
    sent: formatDate(revision.sentAt),
    status: getOfferStatusLabel(t, revision.status, type),
  }));

  const columns = [
    {
      dataField: 'revision',
      text: t(`${translations.offers}:Revision`),
    },
    {
      dataField: 'status',
      text: t(`${translations.offers}:Status`),
    },
    {
      dataField: 'sent',
      text: t(`${translations.offers}:Sent`),
    },
    {
      dataField: 'created',
      text: t(`${translations.common}:Created`),
    },
  ];

  const openRevision = (index: number) => {
    const selected = displayedRevisions[index];

    if (selected.id !== offerId) {
      history.push(`/offers/${selected.id}/${type}`);
    }
  };

  return (
    <>
      <ModalConfirm
        showing={confirmationIsOpen}
        setShowing={() => setConfirmationIsOpen(!confirmationIsOpen)}
        header={t(`${translations.offers}:Create new revision`)}
        text={t(`${translations.offers}:Create new revision confirmation`, {
          revision: currentRevision?.revisionNumber ?? '',
        })}
        yesButton={t(`${translations.common}:Confirm`)}
        noButton={t(`${translations.common}:Cancel`)}
        confirm={createRevision}
      />
      <Row>
        <Col>
          <div
            className="header-text title-spacing"
            onClick={() => setIsOpen(!isOpen)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                setIsOpen(!isOpen);
              }
            }}
            role="button"
            tabIndex={0}
          >
            <img
              alt="toggle-revisions"
              className="collapse-icon"
              src={isOpen ? minimize : maximize}
            />
            {t(`${translations.offers}:Revisions`).toUpperCase()}
            {revisions.length > 1 ? ` (${revisions.length})` : ''}
          </div>
        </Col>
      </Row>
      <Collapse isOpen={isOpen}>
        {revisions.length > 1 ? (
          <BasicTableNoPagination
            columns={columns}
            data={displayedRevisions}
            keyField="id"
            setSelectedRow={openRevision}
          />
        ) : null}
        {canCreate ? (
          <Row>
            <Col>
              <PrimaryButton
                className="button-one-line mt-3"
                disabled={creating}
                float={RIGHT}
                onClick={() => setConfirmationIsOpen(true)}
              >
                {t(
                  `${translations.offers}:Create new revision`
                ).toLocaleUpperCase()}
              </PrimaryButton>
            </Col>
          </Row>
        ) : null}
      </Collapse>
    </>
  );
};

export default OfferRevisions;
